/* eslint-disable indent */
import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Modal } from 'react-bootstrap'
import ScrollArea from 'react-scrollbar'
import FlashDetect from 'flash-detect'
import styled, { css } from 'styled-components'
import {
  TutorialStep,
  InteractiveDevice,
  ImageDevice,
  FlashDevice,
  Html5Device,
  QuickFeedback
} from '..'
import { ErrorBoundary } from '../../containers'
import { ImageCache } from '../ReactPreload'
import messages from './messages'
import LanguageProvider from '../../containers/LanguageProvider/LanguageProvider'
import { OptionsHOC, DeviceNameHOC } from '../../hocs'
import { useAppContext } from '../../containers/App/Context'
import scrollToElement from 'scroll-to-element'
import SEO from '../SEO'
import {
  createSteps,
  createStructuredData,
  stripHtmlTags
} from '../../helpers/utils'

const v4 = css`
  border: none;

  .btn-play {
    width: 60%;
    font-weight: bold;
    color: var(--clr-text-on-button);
    background-color: var(--clr-btn);
    border-radius: var(--button-border-radius);
    height: var(--button-height);
  }
  .step-rewind {
    border: none;
    width: 90%;
    margin-top: 1rem;
  }
  .text-btn {
    text-decoration-line: underline;
    text-underline-offset: 4px;
  }
  .controls {
    border: none;
  }
`

const TutorialGridStyles = styled.div`
  display: ${props => (props.stepData.portrait ? 'grid' : 'block')};
  --columns: 2;
  grid-template-columns: repeat(var(--columns), minmax(auto, 1fr));
  grid-template-rows: 6fr 1fr;
  min-height: ${props => props.dropdownCount * 30}px;
  grid-template-areas:
    'pic steps'
    'controls controls';
  border: 1px solid var(--clr-grey-6-to-text);
  padding: 10px;
  @media (max-width: ${props => (props.botOpen ? '1160px' : '768px')}) {
    --columns: 1;
    grid-template-rows: 1fr;
    grid-template-areas:
      'pic'
      'steps'
      'controls';
  }
  .controls {
    grid-area: controls;
  }
  .pic {
    grid-area: pic;
    justify-content: ${props =>
      props.stepData.portrait ? 'center' : 'flex-start !important'};
    @media (max-width: 700px) {
      display: ${props => (props.stepData.portrait ? '' : 'none !important;')};
    }
  }
  .steps {
    grid-area: steps;
  }
  .touch {
    /* TODO: Need to lighten 0.8 */
    border: 2px solid var(--white);
  }

  .mobile-image-link {
    display: none;
    position: absolute;
    width: 100px;
    background-color: var(--grey-7);
    bottom: -6px;
    left: 37%;
    text-align: center;
    padding: 3px 0;
    color: #1a1a1a;
    font-weight: bold;
    font-size: 12px;
  }
  .mobile-image-link:hover {
    text-decoration: underline;
  }
  .step-rewind {
    border-top: 1px solid var(--clr-grey-6-to-text);
    justify-content: space-between;
    width: 80%;
  }
  ${props => props.version === 'v4' && v4}
  @media (min-width: 1px) and (max-width: 700px) {
    padding: 0;
    .interactive-device {
      display: none;
    }
    .mobile-image-link {
      display: block;
    }
    .float-right {
      padding: 5px;
    }
  }
  .float-right {
    float: right;
  }
  .align-right {
    text-align: right;
    padding: 10px 25px 5px 15px;
  }
  .align-left {
    padding: 10px 15px 5px 25px;
  }
  .align-center {
    border: none !important;
    text-align: center;
    margin-bottom: 10px;
  }
  .align-center > p {
    cursor: pointer;
  }
  .step-ctrl {
    color: var(--clr-accent);
    font-size: var(--h6);
    margin-bottom: 5px;
  }
  .step-ctrl > p {
    display: inline-block;
    cursor: pointer;
  }
  .disabled {
    color: #ccc;
  }
  .scrollarea .scrollbar-container {
    z-index: 45 !important;
  }
`
const TutorialSteps = styled.div`
  margin: 1rem;
`
const StepControls = styled.div`
  margin-top: 2rem;
`
const TutorialControls = styled.div`
  border-top: 1px solid var(--clr-grey-3-to-text);
  padding-top: 20px;
  justify-content: space-between !important;
  margin: 3rem;
`
const StyledQuickFeedback = styled.div`
  #quick-feedback > .body {
    background-color: var(--clr-grey-7-to-text);
    color: var(--clr-grey-3-to-white);
  }
`

const MobileDevice = ({
  show,
  hide,
  stepData,
  playStepAnimation,
  updateStep,
  createUserEvent,
  windowDimensions,
  automatedStep,
  currentPhoneId
}) => {
  return (
    <Modal show={show}>
      <div
        className="flex"
        onClick={() => {
          hide()
        }}
      >
        <InteractiveDevice
          stepData={stepData}
          playStepAnimation={playStepAnimation}
          updateStep={updateStep}
          createUserEvent={createUserEvent}
          windowDimensions={windowDimensions}
          automatedStep={automatedStep}
          currentPhoneId={currentPhoneId}
        />
      </div>
    </Modal>
  )
}

const Tutorial = ({
  data,
  currentStep,
  createUserEvent,
  currentTutorial,
  updateStep,
  playAllStepAnimation,
  playStepAnimation,
  language,
  windowDimensions,
  dropdownCount,
  nextTutorial,
  previousTutorial,
  firstTutorial,
  lastTutorial,
  deviceName,
  currentPhoneId
}) => {
  const [description] = currentTutorial[0].tutorialstep.step.split('.')
  const [showMobileDevice, setShowMobileDevice] = useState(false)
  const scrollAreaRef = useRef(null)
  const { botOpen } = useAppContext()
  const title = currentTutorial[0].tutorial.name
 
  useEffect(() => {
    const offset = document.getElementById('step-active').offsetTop - 175
    if (data.steps.nextStepId === 0) {
      scrollAreaRef.current.scrollYTo(
        document.getElementById('quick-feedback').offsetTop
      )
    } else {
      scrollAreaRef.current.scrollYTo(offset < 0 ? 0 : offset)
    }
  }, [data.id, data.steps.currentId])

  useEffect(() => {
    if (
      ((windowDimensions.width > 700 && data.type === 'INTERACTIVE') ||
        data.type === 'HTML5') &&
      data.imageList.length
    ) {
      ImageCache.stuff(data.imageList)
    }

    createUserEvent({
      category: 'tutorials',
      data: { action: 'View', target: data.id, device_id: currentPhoneId }
    })
  }, [data.id])

  useEffect(() => {
    scrollToElement('.content-top', { duration: 200 })
  }, [])

  const automatedStep = () => {
    const tutorialSteps = currentTutorial
      .map(tutorial => tutorial.tutorialstep)
      .slice(0, -1)
    const automatedStep = tutorialSteps.findIndex(step => {
      return step.swipeOrAction === 'NONE'
    })
    return automatedStep
  }
  const openMobileDevice = () => {
    createUserEvent({
      category: 'tutorials',
      data: {
        action: 'MobileStepImageOpen',
        target: data.id,
        step_to: data.steps.currentId,
        device_id: currentPhoneId
      }
    })
    setShowMobileDevice(true)
  }

  const closeMobileDevice = () => {
    createUserEvent({
      category: 'tutorials',
      data: {
        action: 'MobileStepImageClose',
        target: data.id,
        step_to: data.steps.currentId + 1,
        device_id: currentPhoneId
      }
    })
    setShowMobileDevice(false)
  }
  const playControls = type => {
    switch (type) {
      case 'all':
        createUserEvent({
          category: 'tutorials',
          data: {
            action: 'ControlPlayAll',
            target: data.id,
            step_to: data.steps.currentId,
            device_id: currentPhoneId
          }
        })
        playAllStepAnimation()
        break
      case 'next':
        createUserEvent({
          category: 'tutorials',
          data: {
            action: 'ControlPlayNext',
            target: data.id,
            step_to: data.steps.currentId + 1,
            device_id: currentPhoneId
          }
        })
        updateStep(data.steps.nextStepId)
        if (data.steps.nextStepId === automatedStep()) {
          playStepAnimation()
        }
        break
      case 'prev':
        createUserEvent({
          category: 'tutorials',
          data: {
            action: 'ControlPlayPrevious',
            target: data.id,
            step_to: data.steps.currentId - 1,
            device_id: currentPhoneId
          }
        })
        updateStep(data.steps.prevStepId)
        if (data.steps.prevStepId === automatedStep()) {
          playStepAnimation()
        }
        break
      default:
    }
  }
  const selectedStepText = () => {
    const step = data.steps.stepList.find(step => step.arrayLoc === currentStep)
    return step ? step.text : ''
  }

  const { versionKey: version } = useAppContext()
  const translated = messages[language]
  const flashObj = new FlashDetect()
  const device = deviceName(currentPhoneId, true)
  const steps = createSteps(
    currentTutorial.filter(it => it.tutorialstep.step),
    'tutorialstep.step'
  )

  return (
    <ErrorBoundary>
      <div
        tabIndex="1"
        aria-label="Interactive Tutorial"
        className="animated fadeIn"
        itemProp="step"
        itemScope
      >
        <SEO
          title={`${title} | ${device}`}
          image={data.phoneImage.imageLocation}
          description={stripHtmlTags(description)}
          script={createStructuredData(
            title,
            device,
            data.phoneImage.imageLocation,
            steps
          )}
        />
        <TutorialGridStyles
          id="tutorial-content"
          dropdownCount={dropdownCount}
          botOpen={botOpen}
          stepData={data}
          version={version}
        >
          <div className="pic flex">
            <Choose>
              <When condition={data.type === 'INTERACTIVE'}>
                <InteractiveDevice
                  stepData={data}
                  playStepAnimation={playStepAnimation}
                  updateStep={updateStep}
                  automatedStep={automatedStep}
                  createUserEvent={createUserEvent}
                  windowDimensions={windowDimensions}
                  currentStep={currentStep}
                  deviceName={device}
                  currentPhoneId={currentPhoneId}
                />
                <If condition={windowDimensions.width <= 700}>
                  <MobileDevice
                    show={showMobileDevice}
                    hide={closeMobileDevice}
                    stepData={data}
                    playStepAnimation={playStepAnimation}
                    updateStep={updateStep}
                    createUserEvent={createUserEvent}
                    windowDimensions={windowDimensions}
                    automatedStep={automatedStep}
                    deviceName={device}
                    currentPhoneId={currentPhoneId}
                  />
                </If>
              </When>
              <When condition={data.type === 'IMAGE'}>
                <ImageDevice stepData={data} />
              </When>
              <When condition={data.type === 'MOBILE'} />
              <When condition={data.type === 'FLASH'}>
                <If condition={flashObj.installed}>
                  <FlashDevice stepData={data} />
                </If>
                <If condition={!flashObj.installed}>
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '25px',
                      color: 'rgba(255,0,0,.4)'
                    }}
                  >
                    {translated.flashError}
                  </div>
                </If>
              </When>
              <When condition={data.type === 'HTML5'}>
                <Html5Device fileSrc={data.packageFile.location} />
              </When>
            </Choose>
          </div>
          <TutorialSteps className="steps">
            <ScrollArea
              speed={0.8}
              horizontal={false}
              style={{ maxHeight: '400px' }}
              contentStyle={{ paddingLeft: '12px', paddingRight: '20px' }}
              smoothScrolling
              ref={scrollAreaRef}
              verticalScrollbarStyle={{ "borderRadius": "20px" }}
              verticalContainerStyle={{ "borderRadius": "20px" }}
            >
              <ul className="list-group">
                {data.steps.stepList.map((step, index) => (
                  <TutorialStep
                    key={index}
                    text={step.text}
                    stepNum={index + 1}
                    createUserEvent={createUserEvent}
                    active={step.active}
                    alreadyComplete={step.completed}
                    lastStep={step.last}
                    openMobileDevice={openMobileDevice}
                    updateStep={updateStep}
                    stepArrayLoc={step.arrayLoc}
                    language={language}
                    interactive={data.type === 'INTERACTIVE'}
                    automatedStep={automatedStep}
                    playStepAnimation={playStepAnimation}
                    currentTutorialId={data.id}
                    version={version}
                    currentPhoneId={currentPhoneId}
                  />
                ))}
              </ul>
              <If condition={data.steps.nextStepId === 0}>
                <StyledQuickFeedback>
                  <QuickFeedback
                    type="tutorials"
                    contentId={data.id}
                    createUserEvent={createUserEvent}
                  />
                </StyledQuickFeedback>
              </If>
            </ScrollArea>
            <StepControls className="flex column">
              <div className={`full-width step-ctrl flex ${data.isPlaying && 'disabled'}`}>
                <button
                  className="btn-styles btn-play"
                  onClick={() => (data.isPlaying ? null : playControls('all'))}
                >
                  {translated.playAll}
                </button>
              </div>
              <div className="flex step-rewind">
                <div
                  className={`step-ctrl ${!data.steps.currentId && 'disabled'}`}
                >
                  <button
                    aria-label={selectedStepText()}
                    className="btn-styles text-btn"
                    onClick={() =>
                      !data.steps.currentId ? null : playControls('prev')
                    }
                  >
                    {translated.prev}
                  </button>
                </div>
                <div
                  className={`step-ctrl ${!data.steps.nextStepId &&
                    'disabled'}`}
                >
                  <button
                    aria-label={selectedStepText()}
                    className="btn-styles text-btn"
                    onClick={() =>
                      !data.steps.nextStepId ? null : playControls('next')
                    }
                  >
                    {translated.next}
                  </button>
                </div>
              </div>
            </StepControls>
          </TutorialSteps>
          <TutorialControls className="step-ctrl flex controls">
            <button
              className="btn-styles text-btn"
              style={firstTutorial ? { color: '#ccc' } : null}
              disabled={firstTutorial}
              onClick={() => previousTutorial()}
            >
              {translated.previousTutorial}
            </button>
            <button
              style={lastTutorial ? { color: '#ccc' } : {}}
              className="btn-styles text-btn"
              disabled={lastTutorial}
              onClick={() => nextTutorial()}
            >
              {translated.nextTutorial}
            </button>
          </TutorialControls>
        </TutorialGridStyles>
      </div>
    </ErrorBoundary>
  )
}

export default LanguageProvider(OptionsHOC(DeviceNameHOC(Tutorial)))



// WEBPACK FOOTER //
// ./src/components/Tutorial/Tutorial.js